import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminTopNav from "@/components/AdminTopNav";
import { LayoutDashboard, Store, Package, Tag, ClipboardList, Users, UserCog, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AdminDashboard from "@/components/admin/AdminDashboard";
import AdminBrands from "@/components/admin/AdminBrands";
import AdminProducts from "@/components/admin/AdminProducts";
import AdminOffers from "@/components/admin/AdminOffers";
import AdminOrders from "@/components/admin/AdminOrders";
import AdminCustomers from "@/components/admin/AdminCustomers";
import AdminStaff from "@/components/admin/AdminStaff";
import AdminSettings from "@/components/admin/AdminSettings";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "brands", label: "Brands", icon: Store },
  { key: "products", label: "Products", icon: Package },
  { key: "offers", label: "Offers", icon: Tag },
  { key: "orders", label: "Orders", icon: ClipboardList },
  { key: "customers", label: "Customers", icon: Users },
  { key: "staff", label: "Salesmen", icon: UserCog },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function Admin() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  // Tab + brand live in the URL (same as the Store) so Back returns to the previous screen.
  const [params, setParams] = useSearchParams();
  const active = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "dashboard";
  const productsBrand = active === "products" ? params.get("brand") : null;
  const setActive = (tab, brand = null) => {
    const next = { tab };
    if (brand) next.brand = brand;
    setParams(next);
    window.scrollTo({ top: 0 });
  };
  const setProductsBrand = (brand) => { if (!brand) setActive("products"); };
  const [customersFilter, setCustomersFilter] = useState("all");
  const [customersFilterNonce, setCustomersFilterNonce] = useState(0);

  const openBrandProducts = (brandName) => {
    setActive("products", brandName);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/60 to-white">
      <header className="sticky top-0 z-30 border-b border-blue-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <img src="/slm-logo.png" alt="SLM" className="h-9 w-auto object-contain sm:h-11" />
            <div className="leading-tight">
              <p className="text-sm font-bold text-blue-800 sm:text-base">Admin Panel</p>
              <p className="hidden text-[11px] font-medium text-slate-500 sm:block">Sri Lakshmi Agencies · Ramakuppam</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block"><AdminTopNav current="admin" onLogout={handleLogout} /></div>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-4 pb-2 sm:hidden">
          <AdminTopNav current="admin" onLogout={handleLogout} full />
        </div>

        <nav className="mx-auto max-w-6xl overflow-x-auto px-2 pb-2">
          <div className="flex gap-1">
            {TABS.map((t) => {
              const Icon = t.icon;
              const isActive = active === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`admin-nav-${t.key}`}
                  onClick={() => { setActive(t.key); if (t.key === "customers") setCustomersFilter("all"); }}
                  className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    isActive ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                >
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        <h2 className="mb-4 text-xl font-bold text-slate-800">
          {TABS.find((t) => t.key === active)?.label}
        </h2>
        {active === "dashboard" && (
          <AdminDashboard onGoFilter={(f) => { setCustomersFilter(f); setCustomersFilterNonce((n) => n + 1); setActive("customers"); }} onGoOrders={() => setActive("orders")} />
        )}
        {active === "brands" && <AdminBrands onOpenProducts={openBrandProducts} />}
        {active === "products" && <AdminProducts brandFilter={productsBrand} onClearFilter={() => setProductsBrand(null)} />}
        {active === "offers" && <AdminOffers />}
        {active === "orders" && <AdminOrders />}
        {active === "customers" && <AdminCustomers initialFilter={customersFilter} filterNonce={customersFilterNonce} />}
        {active === "staff" && <AdminStaff />}
        {active === "settings" && <AdminSettings />}
      </main>
    </div>
  );
}
